import AppLayout from '@/Layouts/AppLayout';
import { Card, CardContent } from '@/Components/ui/card';
import { Section, SectionHeader } from '@/Components/Section';
import { FadeIn, StaggerChildren, staggerItem, motion } from '@/Components/motion';
import { Link } from 'react-router-dom';
import { Button } from '@/Components/ui/button';
import { siteConfig } from '@/config/site';
import {
    Target,
    Eye,
    Building2,
    MonitorSmartphone,
    Globe,
    TrendingUp,
    Shield,
    Award,
    Lightbulb,
    HeadphonesIcon,
    ArrowRight,
    ChevronRight,
} from 'lucide-react';

const milestones = [
    {
        year: '2021',
        title: 'Foundation',
        description:
            `${siteConfig.brand.name} was founded with a singular mission: to bridge the gap between talented traders and institutional-grade capital.`,
        icon: Building2,
    },
    {
        year: '2022',
        title: 'Platform Excellence',
        description:
            `Partnered with ${siteConfig.platform.name} to deliver a world-class trading infrastructure, setting new standards for execution speed and reliability.`,
        icon: MonitorSmartphone,
    },
    {
        year: '2023',
        title: 'Global Expansion',
        description:
            'Expanded operations to over 100 countries, empowering traders worldwide with access to elite funding opportunities.',
        icon: Globe,
    },
    {
        year: '2024',
        title: 'Industry Leadership',
        description:
            'Surpassed $500M+ in funded capital, solidifying our position as a trusted leader in the proprietary trading industry.',
        icon: TrendingUp,
    },
];

const leaders = [
    {
        name: 'James Anderson',
        role: 'Chief Executive Officer',
        initials: 'JA',
        bio: 'Former Goldman Sachs executive with 18+ years of experience in global financial markets and institutional trading strategies.',
    },
    {
        name: 'Sarah Mitchell',
        role: 'Chief Technology Officer',
        initials: 'SM',
        bio: 'Fintech visionary and technology leader with 15+ years of experience building scalable trading platforms and infrastructure.',
    },
    {
        name: 'Michael Chen',
        role: 'Head of Trading',
        initials: 'MC',
        bio: 'Seasoned trading professional with 22+ years of expertise across equities, forex, and derivatives markets worldwide.',
    },
];

const values = [
    {
        title: 'Integrity',
        description:
            'Transparency and honesty form the bedrock of every decision we make. Our traders trust us because we earn it every day.',
        icon: Shield,
    },
    {
        title: 'Excellence',
        description:
            'We hold ourselves to the highest standards in technology, risk management, and trader support — mediocrity is never an option.',
        icon: Award,
    },
    {
        title: 'Innovation',
        description:
            'We continuously push boundaries, leveraging cutting-edge technology and data-driven insights to stay ahead of the curve.',
        icon: Lightbulb,
    },
    {
        title: 'Support',
        description:
            'Every trader in our ecosystem receives personalized guidance, resources, and mentorship to maximize their potential.',
        icon: HeadphonesIcon,
    },
];

export default function About() {
    return (
        <AppLayout title={`About ${siteConfig.brand.name} - Elite Prop Trading Firm`}>
            {/* Hero */}
            <section className="relative bg-background pt-32 lg:pt-40 pb-20">
                <div className="bg-grid absolute inset-0 pointer-events-none" />
                <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <div className="text-center max-w-4xl mx-auto">
                        <FadeIn delay={0.1}>
                            <p className="text-primary font-semibold tracking-widest uppercase text-sm mb-4">
                                Our Story
                            </p>
                        </FadeIn>
                        <FadeIn delay={0.2}>
                            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-foreground mb-6">
                                About{' '}
                                <span className="text-gradient">{siteConfig.brand.name}</span>
                            </h1>
                        </FadeIn>
                        <FadeIn delay={0.3}>
                            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
                                We are leading the evolution of proprietary trading — connecting
                                exceptional talent with institutional-grade capital, technology, and
                                opportunity on a global scale.
                            </p>
                        </FadeIn>
                    </div>
                </div>
            </section>

            {/* Mission & Vision */}
            <Section className="bg-muted/30 border-y border-border">
                <FadeIn>
                    <SectionHeader
                        title="What Drives"
                        highlight="Us"
                        description="Our mission and vision define every product we build, every trader we fund, and every decision we make."
                    />
                </FadeIn>
                <StaggerChildren className="grid md:grid-cols-2 gap-8">
                    <motion.div variants={staggerItem}>
                        <Card className="bg-card/80 backdrop-blur-sm border-border hover:border-primary/20 transition-colors rounded-xl p-0 h-full">
                            <CardContent className="p-8 pt-8">
                                <div className="flex items-center gap-4 mb-6">
                                    <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 border border-primary/10">
                                        <Target className="h-6 w-6 text-primary" />
                                    </div>
                                    <h3 className="text-2xl font-bold text-foreground">
                                        Our Mission
                                    </h3>
                                </div>
                                <p className="text-muted-foreground leading-relaxed">
                                    To democratize access to institutional-grade trading capital,
                                    empowering skilled traders around the world to reach their full
                                    potential without risking their own funds. We believe talent
                                    should never be limited by capital.
                                </p>
                            </CardContent>
                        </Card>
                    </motion.div>
                    <motion.div variants={staggerItem}>
                        <Card className="bg-card/80 backdrop-blur-sm border-border hover:border-primary/20 transition-colors rounded-xl p-0 h-full">
                            <CardContent className="p-8 pt-8">
                                <div className="flex items-center gap-4 mb-6">
                                    <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 border border-primary/10">
                                        <Eye className="h-6 w-6 text-primary" />
                                    </div>
                                    <h3 className="text-2xl font-bold text-foreground">
                                        Our Vision
                                    </h3>
                                </div>
                                <p className="text-muted-foreground leading-relaxed">
                                    To become the world's most respected proprietary trading firm
                                    — known for fair evaluations, cutting-edge technology, and an
                                    unwavering commitment to the success of every trader in our
                                    ecosystem.
                                </p>
                            </CardContent>
                        </Card>
                    </motion.div>
                </StaggerChildren>
            </Section>

            {/* Journey Timeline */}
            <Section className="bg-background">
                <FadeIn>
                    <SectionHeader
                        title="Our"
                        highlight="Journey"
                        description="From a bold idea to a global trading powerhouse — every milestone reflects our relentless pursuit of excellence."
                    />
                </FadeIn>
                <div className="relative max-w-3xl mx-auto">
                    {/* Vertical line */}
                    <div className="absolute left-4 md:left-6 top-0 bottom-0 w-px bg-border" />

                    <StaggerChildren className="space-y-10">
                        {milestones.map((milestone) => {
                            const Icon = milestone.icon;
                            return (
                                <motion.div
                                    key={milestone.year}
                                    variants={staggerItem}
                                    className="relative flex items-start gap-6 md:gap-8 pl-4 md:pl-6"
                                >
                                    {/* Dot */}
                                    <div className="relative z-10 flex h-8 w-8 shrink-0 items-center justify-center">
                                        <div className="h-3 w-3 rounded-full bg-primary ring-4 ring-background" />
                                    </div>

                                    {/* Content */}
                                    <Card className="bg-card/80 backdrop-blur-sm border-border hover:border-primary/20 transition-colors rounded-xl p-0 flex-1">
                                        <CardContent className="p-6 pt-6">
                                            <div className="flex items-center gap-3 mb-3">
                                                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 border border-primary/10">
                                                    <Icon className="h-5 w-5 text-primary" />
                                                </div>
                                                <span className="text-primary font-bold text-lg">
                                                    {milestone.year}
                                                </span>
                                            </div>
                                            <h3 className="text-xl font-bold text-foreground mb-2">
                                                {milestone.title}
                                            </h3>
                                            <p className="text-muted-foreground text-sm leading-relaxed">
                                                {milestone.description}
                                            </p>
                                        </CardContent>
                                    </Card>
                                </motion.div>
                            );
                        })}
                    </StaggerChildren>
                </div>
            </Section>

            {/* Leadership Team */}
            <Section className="bg-muted/30 border-y border-border">
                <FadeIn>
                    <SectionHeader
                        title="Leadership"
                        highlight="Team"
                        description="Guided by decades of combined experience across global financial markets, technology, and trading operations."
                    />
                </FadeIn>
                <StaggerChildren className="grid md:grid-cols-3 gap-8">
                    {leaders.map((leader) => (
                        <motion.div key={leader.name} variants={staggerItem}>
                            <Card className="bg-card/80 backdrop-blur-sm border-border hover:border-primary/20 transition-colors rounded-xl p-0 text-center h-full">
                                <CardContent className="p-8 pt-8">
                                    <div className="flex h-20 w-20 items-center justify-center rounded-full bg-primary/10 border border-primary/10 mx-auto mb-6">
                                        <span className="text-2xl font-bold text-primary">
                                            {leader.initials}
                                        </span>
                                    </div>
                                    <h3 className="text-xl font-bold text-foreground mb-1">
                                        {leader.name}
                                    </h3>
                                    <p className="text-primary font-medium text-sm mb-4">
                                        {leader.role}
                                    </p>
                                    <p className="text-muted-foreground text-sm leading-relaxed">
                                        {leader.bio}
                                    </p>
                                </CardContent>
                            </Card>
                        </motion.div>
                    ))}
                </StaggerChildren>
            </Section>

            {/* Core Values */}
            <Section className="bg-background">
                <FadeIn>
                    <SectionHeader
                        title="Core"
                        highlight="Values"
                        description="The principles that guide everything we do — from platform development to trader relationships."
                    />
                </FadeIn>
                <StaggerChildren className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                    {values.map((value) => {
                        const Icon = value.icon;
                        return (
                            <motion.div key={value.title} variants={staggerItem}>
                                <Card className="bg-card/80 backdrop-blur-sm border-border hover:border-primary/20 transition-colors rounded-xl p-0 h-full">
                                    <CardContent className="p-6 pt-6 text-center">
                                        <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-primary/10 border border-primary/10 mx-auto mb-5">
                                            <Icon className="h-7 w-7 text-primary" />
                                        </div>
                                        <h3 className="text-lg font-bold text-foreground mb-2">
                                            {value.title}
                                        </h3>
                                        <p className="text-muted-foreground text-sm leading-relaxed">
                                            {value.description}
                                        </p>
                                    </CardContent>
                                </Card>
                            </motion.div>
                        );
                    })}
                </StaggerChildren>
            </Section>

            {/* CTA */}
            <Section className="bg-muted/30 border-t border-border">
                <FadeIn>
                    <div className="text-center max-w-3xl mx-auto">
                        <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-foreground mb-4">
                            Join the{' '}
                            <span className="text-gradient">Elite</span>
                        </h2>
                        <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                            Ready to trade with institutional-grade capital? Explore our funding
                            programs and take the first step toward becoming a {siteConfig.brand.name} funded
                            trader.
                        </p>
                        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                            <Link to="/funding/overview">
                                <Button size="lg">
                                    View Programs
                                    <ArrowRight className="ml-2 h-5 w-5" />
                                </Button>
                            </Link>
                            <Link to="/contact">
                                <Button variant="outline" size="lg">
                                    Contact Us
                                    <ChevronRight className="ml-2 h-5 w-5" />
                                </Button>
                            </Link>
                        </div>
                    </div>
                </FadeIn>
            </Section>
        </AppLayout>
    );
}
